import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  Output,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ALT_TEXT,
  ARIA_LABELS,
  COMMON_CONSTANTS,
  IMAGE_PATHS,
} from 'src/app/core/constants/common.constant';
import { SIDEBAR_CONST } from 'src/app/core/constants/layout.constants';
import { Route } from 'src/app/core/enums/route.enum';
import { UserRole } from 'src/app/core/enums/user-roles.enum';
import { SidebarItem } from 'src/app/core/interfaces/common.interface';
import { AuthLogoutService } from 'src/app/services/auth/auth-logout.service';
import { LogoutFeedbackFlowService } from 'src/app/services/auth/logout-feedback-flow.service';
import { UserService } from 'src/app/services/user/user.service';
import { PartySearchContextService } from 'src/app/services/party-search/party-search-context.service';
import { canRoleAccessRoutePath } from 'src/app/core/utils/route-access.util';
import { ThemeService } from 'src/app/services/theme/theme.service';

/**
 * Primary navigation rail: role-filtered {@link RouterLink} entries, profile/dark-mode/logout
 * shortcuts in the header block, and collapse/close events for the parent layout.
 * Items are gated with {@link canRoleAccessRoutePath} using {@link UserService#userRoleGate}
 * and {@link PartySearchContextService#params} for CNH/Admin customer routes.
 *
 * Internal portal roles ({@link UserRole.Admin}, {@link UserRole.CNHUser}) use the Figma
 * Search + customer rail. **Admin** accordion is {@link UserRole.Admin} only.
 * {@link UserRole.CNHUser} sees customer FAQ, Contact Us, Request A Change, and Customer Feedback
 * under Search after a successful party search. Admin keeps portal FAQ/Contact in the accordion.
 */

/** Same order as the regular customer sidebar (`sidebarItems`). */
const IMPERSONATED_CUSTOMER_NAV_ROUTE_ORDER: readonly Route[] = [
  Route.DASHBOARD,
  Route.PAYOUT,
  Route.CHANGE_PAYMENT_METHOD,
  Route.CUSTOMER_REQUEST_CHANGE,
  Route.REPAYMENT_SCHEDULE,
  Route.STATEMENT,
  Route.FAQ,
  Route.CONTACT_US,
  Route.DISCOVER,
  Route.CUSTOMER_FEEDBACK_USER,
];

const IMPERSONATION_USER_ONLY_ROUTES = new Set<Route>([
  Route.CUSTOMER_REQUEST_CHANGE,
  Route.CUSTOMER_FEEDBACK_USER,
  Route.FAQ,
  Route.CONTACT_US,
]);

/**
 * First path segment for routes that appear under the Admin accordion (Admin role).
 * When one of these is active, closing the accordion must not restyle Search as the black “selected” row.
 */
const ADMIN_ACCORDION_FIRST_SEGMENTS = new Set<string>([
  Route.USER_MANAGEMENT,
  Route.ANNOUNCEMENT,
  Route.CUSTOMER_FEEDBACK,
  Route.UPLOAD_MARKETING_BANNER,
  Route.ADMIN_TEMPLATES,
  Route.EDIT_TEMPLATE,
  Route.NOTIFICATIONS,
  Route.ADMIN_CONTACT_US,
  Route.ADMIN_FAQ,
  Route.MANAGE_CATEGORY,
  Route.REQUEST_CHANGE,
]);

const HEADER_NOTIFICATIONS_VIEW_SOURCE = 'header-notification-view-all';

function firstUrlSegment(rawUrl: string): string {
  const path = rawUrl.split('?')[0].replace(/^\//, '');
  return path.split('/')[0] ?? '';
}

function querySource(rawUrl: string): string | null {
  const query = rawUrl.split('?')[1]?.split('#')[0] ?? '';
  if (!query) {
    return null;
  }

  return new URLSearchParams(query).get('source');
}

function isHeaderNotificationsViewSource(rawUrl: string): boolean {
  return querySource(rawUrl) === HEADER_NOTIFICATIONS_VIEW_SOURCE;
}

function isAdminAccordionRouteSegment(segment: string): boolean {
  return !!segment && ADMIN_ACCORDION_FIRST_SEGMENTS.has(segment);
}

@Component({
  selector: 'app-sidebar',
  host: { class: 'block h-full' },
  imports: [NgClass, RouterLink, RouterLinkActive],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent {
  /** Desktop: parent applies narrow (icon-only) styling when true. */
  @Input() isCollapsed = false;
  /** Lets the template align with dashboard-specific chrome from {@link MainLayoutComponent}. */
  @Input() isDashboardPage = false;

  /** Mobile drawer: request close after navigation or logout. */
  @Output() readonly closeSidebar = new EventEmitter<void>();
  /** Desktop: parent toggles {@link isCollapsed} when the user hits the rail control. */
  @Output() readonly toggleCollapse = new EventEmitter<void>();

  /** Index of the nav row under pointer; drives hover icon swaps in the default rail template. */
  hoveredIndex: number | null = null;
  readonly collapsedTooltipVisible = signal(false);
  readonly collapsedTooltipLabel = signal('');
  readonly collapsedTooltipTop = signal(0);
  readonly collapsedTooltipLeft = signal(0);

  readonly router = inject(Router);
  readonly userService = inject(UserService);
  readonly themeService = inject(ThemeService);

  /** Exposed for template: assets, copy, ARIA, and enum for special link handling. */
  readonly IMAGE_PATHS = IMAGE_PATHS;
  readonly SIDEBAR_CONST = SIDEBAR_CONST;
  readonly ALT_TEXT = ALT_TEXT;
  readonly ARIA_LABELS = ARIA_LABELS;
  readonly COMMON_CONSTANTS = COMMON_CONSTANTS;
  readonly Route = Route;

  /** True while customer shortcut links under Search are shown (party context; independent of route). */
  readonly customerSearchGroupOpen = signal(true);

  /** True while the Admin accordion is open; submenu renders only when the sidebar is expanded. */
  readonly adminSectionOpen = signal(false);

  /**
   * Top-of-sidebar actions (profile, dark mode stub route, logout); not merged into the main nav list.
   */
  readonly sidebarHeaderItems: readonly SidebarItem[] = [
    {
      route: Route.MY_PROFILE,
      label: SIDEBAR_CONST.myProfile,
      alt: ALT_TEXT.MY_PROFILE_ICON,
      icon: IMAGE_PATHS.PROFILE_DARK,
      iconActive: IMAGE_PATHS.PROFILE_ACTIVE,
    },
    {
      route: Route.DARK_MODE,
      label: SIDEBAR_CONST.darkMode,
      alt: ALT_TEXT.DARK_MODE,
      icon: IMAGE_PATHS.HALF_MOON,
    },
    {
      route: Route.LOG_OUT,
      label: SIDEBAR_CONST.logout,
      alt: ALT_TEXT.LOGOUT,
      icon: IMAGE_PATHS.LOG_OUT,
    },
  ];

  /** Full catalogue of app routes shown in the rail; nav lists filter by role. */
  readonly allSidebarItems: readonly SidebarItem[] = [
    {
      route: Route.SEARCH_USER,
      label: SIDEBAR_CONST.searchUser,
      alt: ALT_TEXT.SEARCH_USER,
      icon: IMAGE_PATHS.SEARCH_ICON,
      iconActive: IMAGE_PATHS.SEARCH_WHITE_ICON,
      iconHover: IMAGE_PATHS.SEARCH_ICON,
    },
    {
      route: Route.DASHBOARD,
      label: SIDEBAR_CONST.dashboard,
      alt: ALT_TEXT.DASHBOARD,
      icon: IMAGE_PATHS.DASHBOARD,
      iconActive: IMAGE_PATHS.DASHBOARD_ACTIVE,
      iconHover: IMAGE_PATHS.DASHBOARD_RED,
    },
    {
      route: Route.PAYOUT,
      label: SIDEBAR_CONST.payoutRequest,
      alt: ALT_TEXT.PAYOUT_REQUEST,
      icon: IMAGE_PATHS.PAYOUT,
      iconActive: IMAGE_PATHS.PAYOUT_ACTIVE,
      iconHover: IMAGE_PATHS.PAYOUT_RED,
    },
    {
      route: Route.CHANGE_PAYMENT_METHOD,
      label: SIDEBAR_CONST.changePaymentMethod,
      alt: ALT_TEXT.CHANGE_PAYMENT_METHOD,
      icon: IMAGE_PATHS.PAYMENT_METHOD,
      iconActive: IMAGE_PATHS.PAYMENT_METHOD_ACTIVE,
      iconHover: IMAGE_PATHS.PAYMENT_METHOD_RED,
    },
    {
      route: Route.CUSTOMER_REQUEST_CHANGE,
      label: SIDEBAR_CONST.customerRequestChange,
      alt: ALT_TEXT.REQUEST_CHANGE,
      icon: IMAGE_PATHS.TEMPLATE_ICON,
      iconActive: IMAGE_PATHS.TEMPLATE_ICON_ACTIVE,
      iconHover: IMAGE_PATHS.TEMPLATE_ICON_RED,
    },
    {
      route: Route.REPAYMENT_SCHEDULE,
      label: SIDEBAR_CONST.repaymentSchedule,
      alt: ALT_TEXT.REPAYMENT_SCHEDULE,
      icon: IMAGE_PATHS.REPAYMENT_SCHEDULE,
      iconActive: IMAGE_PATHS.REPAYMENT_ACTIVE,
      iconHover: IMAGE_PATHS.REPAYMENT_SCHEDULE_RED,
    },
    {
      route: Route.STATEMENT,
      label: SIDEBAR_CONST.statement,
      alt: ALT_TEXT.STATEMENT,
      icon: IMAGE_PATHS.STATEMENT,
      iconActive: IMAGE_PATHS.STATEMENT_ACTIVE,
      iconHover: IMAGE_PATHS.STATEMENT_RED,
    },
    {
      route: Route.USER_MANAGEMENT,
      label: SIDEBAR_CONST.userManagement,
      alt: ALT_TEXT.USER_MANAGEMENT,
      icon: IMAGE_PATHS.PROFILE_DARK,
      iconActive: IMAGE_PATHS.PROFILE_ACTIVE,
      iconHover: IMAGE_PATHS.PROFILE_RED,
    },
    {
      route: Route.DROPDOWN_CONFIGURATION,
      label: SIDEBAR_CONST.dropdownConfiguration,
      alt: ALT_TEXT.DROPDOWN_CONFIGURATION_ICON,
      icon: IMAGE_PATHS.DROPDOWN_CONFIGURATION_ICON,
      iconActive: IMAGE_PATHS.DROPDOWN_CONFIGURATION_ACTIVE,
      iconHover: IMAGE_PATHS.DROPDOWN_CONFIGURATION_RED,
    },
    {
      route: Route.ANNOUNCEMENT,
      label: SIDEBAR_CONST.announcement,
      alt: ALT_TEXT.ANNOUNCEMENT_ICON,
      icon: IMAGE_PATHS.ANNOUNCEMENT_ICON,
      iconActive: IMAGE_PATHS.ANNOUNCEMENT_ICON_ACTIVE,
      iconHover: IMAGE_PATHS.ANNOUNCEMENT_ICON_RED,
    },
    {
      route: Route.CUSTOMER_FEEDBACK,
      label: SIDEBAR_CONST.customerFeedback,
      alt: ALT_TEXT.CUSTOMER_FEEDBACK,
      icon: IMAGE_PATHS.CUSTOMER_FEEDBACK_ICON,
      iconActive: IMAGE_PATHS.CUSTOMER_FEEDBACK_ICON,
      iconHover: IMAGE_PATHS.CUSTOMER_FEEDBACK_ICON,
    },
    {
      route: Route.FAQ,
      label: SIDEBAR_CONST.faq,
      alt: ALT_TEXT.FAQ,
      icon: IMAGE_PATHS.FAQ,
      iconActive: IMAGE_PATHS.FAQ_ACTIVE,
      iconHover: IMAGE_PATHS.FAQ_RED,
    },
    {
      route: Route.ADMIN_FAQ,
      label: SIDEBAR_CONST.adminFaq,
      alt: ALT_TEXT.FAQ,
      icon: IMAGE_PATHS.FAQ,
      iconActive: IMAGE_PATHS.FAQ_ACTIVE,
      iconHover: IMAGE_PATHS.FAQ_RED,
    },
    {
      route: Route.REQUEST_CHANGE,
      label: SIDEBAR_CONST.requestChange,
      alt: ALT_TEXT.REQUEST_CHANGE,
      icon: IMAGE_PATHS.TEMPLATE_ICON,
      iconActive: IMAGE_PATHS.TEMPLATE_ICON_ACTIVE,
      iconHover: IMAGE_PATHS.TEMPLATE_ICON_RED,
    },
    {
      route: Route.CONTACT_US,
      label: SIDEBAR_CONST.contactUs,
      alt: ALT_TEXT.CONTACT_US,
      icon: IMAGE_PATHS.CONTACT_US,
      iconActive: IMAGE_PATHS.CONTACT_US_ACTIVE,
      iconHover: IMAGE_PATHS.CONTACT_US_RED,
    },
    {
      route: Route.ADMIN_CONTACT_US,
      label: SIDEBAR_CONST.contactUs,
      alt: ALT_TEXT.CONTACT_US,
      icon: IMAGE_PATHS.CONTACT_US,
      iconActive: IMAGE_PATHS.CONTACT_US_ACTIVE,
      iconHover: IMAGE_PATHS.CONTACT_US_RED,
    },
    {
      route: Route.UPLOAD_MARKETING_BANNER,
      label: SIDEBAR_CONST.uploadMarketingBanner,
      alt: ALT_TEXT.UPLOAD_MARKETING_BANNER,
      icon: IMAGE_PATHS.FILE_DARK_ICON,
      iconActive: IMAGE_PATHS.FILE_DARK_ACTIVE,
      iconHover: IMAGE_PATHS.FILE_DARK_RED,
    },
    {
      route: Route.ADMIN_TEMPLATES,
      label: SIDEBAR_CONST.templates,
      alt: ALT_TEXT.TEMPLATE_ICON,
      icon: IMAGE_PATHS.TEMPLATE_ICON,
      iconActive: IMAGE_PATHS.TEMPLATE_ICON_ACTIVE,
      iconHover: IMAGE_PATHS.TEMPLATE_ICON_RED,
    },
    {
      route: Route.DISCOVER,
      label: SIDEBAR_CONST.discover,
      alt: ALT_TEXT.DISCOVER,
      icon: IMAGE_PATHS.DISCOVER,
      iconActive: IMAGE_PATHS.DISCOVER_ACTIVE,
      iconHover: IMAGE_PATHS.DISCOVER_RED,
    },
    {
      route: Route.CUSTOMER_FEEDBACK_USER,
      label: SIDEBAR_CONST.customerFeedback,
      alt: ALT_TEXT.CUSTOMER_FEEDBACK,
      icon: IMAGE_PATHS.CUSTOMER_FEEDBACK_ICON,
      iconActive: IMAGE_PATHS.CUSTOMER_FEEDBACK_ICON_ACTIVE,
      iconHover: IMAGE_PATHS.CUSTOMER_FEEDBACK_ICON_RED,
    },
  ];

  /** Same internal cohort as {@link internalPartyContextGuard} (search + impersonation flows). */
  readonly isInternalPortalSidebar = computed(() => {
    const readAccess = this.userService.userRoleGate();
    return readAccess === UserRole.Admin || readAccess === UserRole.CNHUser;
  });

  /** Admin accordion + submenu (Figma 608:22309): {@link UserRole.Admin} only. */
  readonly showAdminAccordion = computed(() => this.userService.userRoleGate() === UserRole.Admin);

  /** Collapse/expand customer shortcuts under Search: {@link UserRole.Admin} only. */
  readonly showCustomerSearchCollapse = computed(() => this.showAdminAccordion());

  /**
   * Customer can navigate to party routes: matches
   * `internalPartyContextGuard` (`params` + `contractData`).
   */
  readonly partyImpersonationActive = computed(() => {
    return (
      this.partySearchContext.params() !== null && this.partySearchContext.contractData() !== null
    );
  });

  /**
   * Customer nav under Search while impersonating.
   * Uses customer {@link Route.FAQ} and {@link Route.CONTACT_US} — separate from Admin accordion
   * ({@link Route.ADMIN_FAQ} / {@link Route.ADMIN_CONTACT_US}).
   */
  readonly adminCustomerNavItems = computed(() => {
    if (
      !this.isInternalPortalSidebar() ||
      !this.partyImpersonationActive() ||
      this.adminSectionOpen()
    ) {
      return [];
    }
    if (this.showCustomerSearchCollapse() && !this.customerSearchGroupOpen()) {
      return [];
    }
    const role = this.userService.userRoleGate();
    if (!role) {
      return [];
    }
    const partySearchActive = true;
    const byPath = new Map(this.allSidebarItems.map((item) => [item.route, item] as const));

    return IMPERSONATED_CUSTOMER_NAV_ROUTE_ORDER.map((path) => byPath.get(path)).filter(
      (item): item is SidebarItem => {
        if (!item) {
          return false;
        }
        if (IMPERSONATION_USER_ONLY_ROUTES.has(item.route)) {
          return true;
        }
        return canRoleAccessRoutePath(role, this.router.config, item.route, partySearchActive);
      },
    );
  });

  /** Nav links for non-Internal portal roles (unchanged catalogue + gating). */
  readonly sidebarItems = computed(() => {
    if (this.isInternalPortalSidebar()) {
      return [];
    }
    const role = this.userService.userRoleGate();
    if (!role) {
      return [];
    }
    const partySearchActive = this.partySearchContext.params() !== null;
    const items = this.allSidebarItems.filter((item) =>
      canRoleAccessRoutePath(role, this.router.config, item.route, partySearchActive),
    );

    if (
      role === UserRole.CNHMarketingUser &&
      canRoleAccessRoutePath(role, this.router.config, Route.NOTIFICATIONS, partySearchActive)
    ) {
      items.push({
        route: Route.NOTIFICATIONS,
        label: SIDEBAR_CONST.notifications,
        alt: ALT_TEXT.NOTIFICATION_ICON,
        icon: IMAGE_PATHS.NOTIFICATION,
        iconActive: IMAGE_PATHS.NOTIFICATION_ACTIVE,
        iconHover: IMAGE_PATHS.NOTIFICATION_RED,
      });
    }

    if (role === UserRole.CNHMarketingUser) {
      const faqIndex = items.findIndex(
        (item) => item.route === Route.FAQ || item.route === Route.ADMIN_FAQ,
      );
      if (faqIndex >= 0) {
        const [faqItem] = items.splice(faqIndex, 1);
        items.push(faqItem);
      }
    }

    return items;
  });

  readonly adminSubMenuItems = computed((): SidebarItem[] => {
    if (!this.isInternalPortalSidebar() || !this.showAdminAccordion()) {
      return [];
    }
    const role = this.userService.userRoleGate();
    if (!role) {
      return [];
    }
    const partySearchActive = this.partySearchContext.params() !== null;
    const byPath = new Map(this.allSidebarItems.map((item) => [item.route, item] as const));

    const resolve = (path: Route): SidebarItem | null => {
      const item = byPath.get(path);
      if (!item || !canRoleAccessRoutePath(role, this.router.config, path, partySearchActive)) {
        return null;
      }
      return item;
    };

    const notificationItem: SidebarItem = {
      route: Route.NOTIFICATIONS,
      label: SIDEBAR_CONST.notifications,
      alt: ALT_TEXT.NOTIFICATION_ICON,
      icon: IMAGE_PATHS.NOTIFICATION,
      iconActive: IMAGE_PATHS.NOTIFICATION_ACTIVE,
      iconHover: IMAGE_PATHS.NOTIFICATION_RED,
    };

    const out: SidebarItem[] = [];
    const push = (path: Route) => {
      const item = resolve(path);
      if (item) {
        out.push(item);
      }
    };

    push(Route.USER_MANAGEMENT);
    push(Route.ANNOUNCEMENT);
    push(Route.REQUEST_CHANGE);
    push(Route.CUSTOMER_FEEDBACK);
    push(Route.UPLOAD_MARKETING_BANNER);
    push(Route.ADMIN_TEMPLATES);

    if (canRoleAccessRoutePath(role, this.router.config, Route.NOTIFICATIONS, partySearchActive)) {
      out.push(notificationItem);
    }

    push(Route.ADMIN_FAQ);
    push(Route.ADMIN_CONTACT_US);

    return out;
  });

  /**
   * Search row “primary” dark pill: CNHUser unchanged (always when not impersonation-collapsed layout).
   * Admin: only when the accordion is closed **and** the current page is not an Admin-submenu route
   * (otherwise collapsing Admin was falsely selecting Search while still on e.g. User Management).
   */
  readonly adminSearchRowDark = computed(() => {
    if (!this.isInternalPortalSidebar()) {
      return false;
    }
    if (!this.showAdminAccordion()) {
      return !this.adminSectionOpen();
    }
    if (this.adminSectionOpen()) {
      return false;
    }
    const seg = this.internalNavFirstSegment();
    if (seg && ADMIN_ACCORDION_FIRST_SEGMENTS.has(seg)) {
      return false;
    }
    return true;
  });

  /**
   * Admin header black pill (Figma): when the accordion is expanded, or when it is collapsed but the
   * user remains on an Admin submenu — including the narrow icon rail.
   */
  readonly adminAccordionTriggerDark = computed(() => {
    if (!this.showAdminAccordion()) {
      return false;
    }
    if (this.adminSectionOpen()) {
      return true;
    }
    const seg = this.internalNavFirstSegment();
    return !!(seg && ADMIN_ACCORDION_FIRST_SEGMENTS.has(seg));
  });

  readonly partySearchContext = inject(PartySearchContextService);

  // Used for the mobile-only impersonation block.
  readonly displayUserName = computed(() => this.partySearchContext.impersonatedUserName() ?? '');

  readonly displayUserEmail = computed(() => {
    const data = this.partySearchContext.impersonatedUserApiData();
    if (!data) {
      return null;
    }
    return data.Email ?? data.BusinessEmail ?? data.UserEmail ?? null;
  });

  private readonly authLogout = inject(AuthLogoutService);
  private readonly logoutFeedbackFlow = inject(LogoutFeedbackFlowService);
  private readonly internalNavFirstSegment = signal(firstUrlSegment(this.router.url));
  private readonly headerNotificationsReadOnlyView = signal(
    isHeaderNotificationsViewSource(this.router.url),
  );

  constructor() {
    const router = this.router;
    const destroyRef = inject(DestroyRef);
    effect(() => {
      if (!this.partyImpersonationActive()) {
        untracked(() => this.customerSearchGroupOpen.set(true));
      }
    });
    effect(() => {
      if (!this.showAdminAccordion()) {
        return;
      }
      const segment = this.internalNavFirstSegment();
      untracked(() => this.syncAdminSectionOpenFromRoute(segment));
    });
    router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe((e) => {
        this.internalNavFirstSegment.set(firstUrlSegment(e.urlAfterRedirects));
        this.headerNotificationsReadOnlyView.set(
          isHeaderNotificationsViewSource(e.urlAfterRedirects),
        );
      });
  }

  /** Stable key for `*ngFor` / `@for` over {@link sidebarItems} under OnPush. */
  trackByRoute = (_: number, item: SidebarItem) => item.route;

  onNavItemMouseEnter(event: MouseEvent, index: number, label: string): void {
    this.hoveredIndex = index;

    if (!this.isCollapsed) {
      this.collapsedTooltipVisible.set(false);
      return;
    }

    const anchor = event.currentTarget as HTMLElement | null;
    if (!anchor) {
      this.collapsedTooltipVisible.set(false);
      return;
    }

    const rect = anchor.getBoundingClientRect();
    this.collapsedTooltipLabel.set(label);
    this.collapsedTooltipTop.set(rect.top + rect.height / 2);
    this.collapsedTooltipLeft.set(rect.right + 10);
    this.collapsedTooltipVisible.set(true);
  }

  onNavItemMouseLeave(): void {
    this.hoveredIndex = null;
    this.collapsedTooltipVisible.set(false);
  }

  /** Icon for an Admin submenu row (collapsed rail uses hover swaps like other nav items). */
  adminSubMenuIcon(item: SidebarItem, subIdx: number, isActive: boolean): string {
    if (isActive) {
      // Collapsed active → white icon on black bg; expanded active → dark icon on grey bg
      return this.isCollapsed ? (item.iconActive ?? item.icon) : item.icon;
    }
    if (this.isCollapsed && this.hoveredIndex === subIdx && item.iconHover) {
      return item.iconHover;
    }
    return item.icon;
  }

  /** Maps the Admin accordion FAQ row to the role-appropriate route while keeping the same label/icon. */
  adminSubMenuRoute(item: SidebarItem): Route {
    if (item.route !== Route.FAQ) {
      return item.route;
    }
    return this.showAdminAccordion() ? Route.ADMIN_FAQ : Route.FAQ;
  }

  /** Admin FAQ row should stay active while browsing nested admin FAQ pages (e.g. manage-category). */
  isAdminSubMenuItemActive(item: SidebarItem, routerActive: boolean): boolean {
    if (item.route === Route.NOTIFICATIONS && this.headerNotificationsReadOnlyView()) {
      return false;
    }

    if (routerActive) {
      return true;
    }
    if (item.route !== Route.ADMIN_FAQ || !this.showAdminAccordion()) {
      return false;
    }
    const segment = this.internalNavFirstSegment();
    return segment === Route.ADMIN_FAQ || segment === Route.MANAGE_CATEGORY;
  }

  toggleCustomerSearchGroup(): void {
    if (!this.showCustomerSearchCollapse()) {
      return;
    }
    this.customerSearchGroupOpen.update((open) => !open);
  }

  toggleAdminSection(): void {
    if (!this.showAdminAccordion()) {
      return;
    }
    this.adminSectionOpen.update((open) => {
      const nextOpen = !open;
      if (nextOpen) {
        void this.router.navigate(['/', Route.USER_MANAGEMENT]);
      }
      return nextOpen;
    });
  }

  /**
   * Search is the primary nav when the Admin accordion is closed; reopening Search after Admin
   * must clear the accordion so the black pill / active styling applies again on `/search`.
   */
  onInternalSearchNavClick(): void {
    this.adminSectionOpen.set(false);
    this.closeSidebar.emit();
  }

  /** Logout uses the same AuthLogoutService as the header (optional UI cleanup via beforeLogout). */
  onLogout(): void {
    const logoutOptions = {
      beforeLogout: () => this.closeSidebar.emit(),
    };

    if (this.logoutFeedbackFlow.requestLogoutFeedback(logoutOptions)) {
      return;
    }

    this.authLogout.logout(logoutOptions);
  }

  onDarkMode(): void {
    this.themeService.toggle();
  }

  /** Keeps Admin accordion expanded on portal routes (incl. after full page refresh). */
  private syncAdminSectionOpenFromRoute(segment: string): void {
    if (segment === Route.SEARCH_USER) {
      this.adminSectionOpen.set(false);
      return;
    }
    if (isAdminAccordionRouteSegment(segment)) {
      this.adminSectionOpen.set(true);
    }
  }
}
